import React from 'react';
import { validatePassword, PasswordStrength } from '../../utils/passwordValidation';
import './PasswordStrengthIndicator.css';

interface PasswordStrengthIndicatorProps {
  password: string;
  showDetails?: boolean;
}

const PasswordStrengthIndicator: React.FC<PasswordStrengthIndicatorProps> = ({ 
  password, 
  showDetails = true 
}) => {
  const validation = validatePassword(password);
  const { strength, requirements } = validation;

  if (!password) {
    return null;
  }

  const getStrengthColor = (level: PasswordStrength['level']): string => {
    switch (level) {
      case 'very-weak': return '#ef4444'; // Red
      case 'weak': return '#f97316'; // Orange
      case 'fair': return '#eab308'; // Yellow
      case 'good': return '#22c55e'; // Green
      case 'strong': return '#16a34a'; // Dark Green
      default: return '#6b7280'; // Gray
    }
  };

  const getStrengthLabel = (level: PasswordStrength['level']): string => {
    switch (level) {
      case 'very-weak': return 'Very Weak';
      case 'weak': return 'Weak';
      case 'fair': return 'Fair';
      case 'good': return 'Good';
      case 'strong': return 'Strong';
      default: return '';
    }
  };

  const strengthPercentage = Math.min((strength.score / 4) * 100, 100);
  const strengthColor = getStrengthColor(strength.level);

  return (
    <div className="password-strength-container">
      {/* Strength Bar */}
      <div className="password-strength-bar">
        <div className="strength-label">
          <span className="strength-text">Password Strength: </span>
          <span 
            className="strength-level"
            style={{ color: strengthColor, fontWeight: 600 }}
          >
            {getStrengthLabel(strength.level)}
          </span>
        </div>
        <div className="strength-progress">
          <div 
            className="strength-fill"
            style={{ 
              width: `${strengthPercentage}%`,
              backgroundColor: strengthColor,
              transition: 'all 0.3s ease'
            }}
          />
        </div>
      </div>

      {/* Requirements Checklist */}
      {showDetails && (
        <div className="password-requirements">
          <div className="requirements-header">Password Requirements:</div>
          <div className="requirements-list">
            {requirements.map((requirement, index) => (
              <div 
                key={index}
                className={`requirement-item ${requirement.met ? 'met' : 'unmet'}`}
              >
                <span className="requirement-icon">
                  {requirement.met ? '✓' : '○'}
                </span>
                <span className="requirement-text">
                  {requirement.text}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Feedback Messages */}
      {strength.feedback.length > 0 && (
        <div className="password-feedback">
          {strength.feedback.map((message, index) => (
            <div 
              key={index}
              className={`feedback-message ${strength.level}`}
            >
              {message}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PasswordStrengthIndicator; 